import { useEffect, useRef, useState } from "react"
import { ExternalLink, FileText, Loader2 } from "lucide-react"
import type { AppShell } from "@/App"
import { cn } from "@/lib/utils"
import { fileUrl, receiptDocumentUrl } from "./api"
import { PinchZoomImage } from "./ui"

/**
 * O documento do fornecedor (Fase 2d): cartão com o nome e «Abrir», e a
 * pré-visualização — as fotos (uma por página) ou o PDF página a página.
 *
 * O PDF desenha-se com o pdf.js (carregado só quando há um PDF). Se o browser
 * não o conseguir descarregar (CORS do armazenamento), fica o «Abrir».
 */
export function DocumentPreview({
  shell,
  receiptId,
  fromEmail,
  fileKey,
  mime,
  fileKeys,
  fill = false,
}: {
  shell: AppShell
  receiptId: string
  fromEmail: boolean
  fileKey: string | null
  mime: string | null
  fileKeys: string[]
  /** Telemóvel: ocupa o ecrã, com zoom por gesto nas fotos. */
  fill?: boolean
}) {
  // Várias fotos da mesma fatura: uma página cada. Um email: o documento do caso.
  const keys = fromEmail ? [fileKey ?? "email"] : fileKeys.length > 0 ? fileKeys : fileKey ? [fileKey] : []
  const [page, setPage] = useState(0)
  const [urls, setUrls] = useState<Record<number, string | null>>({})
  const key = keys[page] ?? null

  useEffect(() => {
    if (!key || urls[page] !== undefined) return
    const load = fromEmail ? receiptDocumentUrl(shell, receiptId) : fileUrl(shell, key)
    load.then((u) => setUrls((m) => ({ ...m, [page]: u }))).catch(() => setUrls((m) => ({ ...m, [page]: null })))
  }, [shell, key, page, fromEmail, receiptId, urls])

  if (keys.length === 0) return <p className="p-4 text-sm text-muted-foreground">Sem documento.</p>
  const url = urls[page]
  const isPdf = (mime ?? "").includes("pdf") || String(key).toLowerCase().endsWith(".pdf")
  const name = fromEmail ? "Documento do email" : String(key).split("/").pop() ?? "documento"

  return (
    <div className={cn("flex flex-col overflow-hidden rounded-xl border bg-card", fill && "h-[calc(100dvh-13rem)] min-h-80")}>
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium" title={name}>
          {name}
        </span>
        <button
          type="button"
          disabled={!url}
          onClick={() => url && window.open(url, "_blank")}
          className="inline-flex h-11 items-center gap-1.5 rounded-md px-3 text-sm font-medium hover:bg-muted disabled:opacity-50 sm:h-8"
        >
          <ExternalLink className="h-4 w-4" /> Abrir
        </button>
      </div>
      {keys.length > 1 && (
        <div className="flex gap-1 border-b px-2 py-1.5" role="tablist" aria-label="Páginas">
          {keys.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={page === i}
              onClick={() => setPage(i)}
              className={cn("h-11 min-w-11 rounded-md px-2 text-sm tabular-nums sm:h-8 sm:min-w-8", page === i ? "bg-primary text-primary-foreground" : "hover:bg-muted")}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
      <div className={cn("min-h-0 flex-1 bg-muted/40", !fill && "max-h-[calc(100vh-12rem)] overflow-auto")}>
        {url === undefined ? (
          <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> A abrir o documento…
          </div>
        ) : url === null ? (
          <p className="p-4 text-sm text-muted-foreground">Não foi possível abrir o documento.</p>
        ) : isPdf ? (
          <PdfPages url={url} />
        ) : fill ? (
          <PinchZoomImage src={url} alt={`Documento do fornecedor, página ${page + 1}`} />
        ) : (
          <img src={url} alt={`Documento do fornecedor, página ${page + 1}`} className="w-full object-contain" />
        )}
      </div>
    </div>
  )
}

/** O PDF, todas as páginas, à largura do cartão. */
function PdfPages({ url }: { url: string }) {
  const box = useRef<HTMLDivElement>(null)
  const [state, setState] = useState<"a_abrir" | "ok" | "falhou">("a_abrir")

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      try {
        const [pdfjs, { default: workerUrl }] = await Promise.all([import("pdfjs-dist"), import("pdfjs-dist/build/pdf.worker.min.mjs?url")])
        pdfjs.GlobalWorkerOptions.workerSrc = workerUrl
        const doc = await pdfjs.getDocument({ url }).promise
        const el = box.current
        if (!el || cancelled) return
        el.replaceChildren()
        const width = Math.max(280, el.clientWidth - 16)
        for (let n = 1; n <= Math.min(doc.numPages, 20); n++) {
          const pg = await doc.getPage(n)
          const base = pg.getViewport({ scale: 1 })
          const scale = (width / base.width) * (window.devicePixelRatio || 1)
          const vp = pg.getViewport({ scale })
          const canvas = document.createElement("canvas")
          canvas.width = vp.width
          canvas.height = vp.height
          canvas.style.width = "100%"
          canvas.className = "mb-2 rounded bg-white shadow-sm"
          canvas.setAttribute("aria-label", `Página ${n}`)
          await pg.render({ canvasContext: canvas.getContext("2d")!, viewport: vp }).promise
          if (cancelled) return
          el.appendChild(canvas)
        }
        setState("ok")
      } catch {
        if (!cancelled) setState("falhou")
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [url])

  return (
    <div className="p-2">
      {state === "a_abrir" && (
        <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> A preparar o PDF…
        </div>
      )}
      {state === "falhou" && <p className="p-2 text-sm text-muted-foreground">A pré-visualização do PDF não está disponível aqui — use «Abrir».</p>}
      <div ref={box} />
    </div>
  )
}
